import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  IconButton,
  Tooltip,
  Box,
  useTheme,
  Menu,
  MenuItem,
  CircularProgress,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  ContentCopy,
  Edit,
  Translate,
  Check,
  Autorenew,
  Search,
} from '@mui/icons-material';
import Description from './DescriptionWithClickableTimestamps';
import { ActionObject } from '../../../../../types';
import { RootState } from '../../../../../reducers';
import {
  reprocessSelectedVideo,
  toggleSummaryEdit,
} from '../../../selectedVideoSlice';
import {
  LanguageCode,
  topLanguagesSorted,
  translateText,
} from '../../../translate';
import useLimits from '../../../../plan/hooks/limits';
import FeedbackModal from '../../../FeedbackModal';

type ActionType = 'Copy' | 'Edit' | 'Translate' | 'Reprocess';
type UpdatedActionObject = Omit<
  ActionObject<ActionType, React.ReactNode>,
  'onClick'
> & {
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;
};

export const SummaryViewOnly = () => {
  const theme = useTheme();
  const { selectedVideo } = useSelector((state: RootState) => state);
  const { text } = selectedVideo.summary;
  const dispatch = useDispatch();
  const guardLimits = useLimits();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [translatedText, setTranslatedText] = useState(text);
  const [isTranslating, setIsTranslating] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleUpgrade = () => {
    guardLimits('AISummaries', () => {
      // Handle upgrade logic here, if needed.
    });
  };

  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [copySuccess]);

  const [feedbackOpen, setFeedbackOpen] = useState(false);

  if (!text) return null;

  const handleTranslateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (guardLimits('AISummaries')) {
      setAnchorEl(event.currentTarget);
    } else {
      handleUpgrade();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const translate = async (targetLang: LanguageCode) => {
    if (guardLimits('AISummaries')) {
      setIsTranslating(true);
      const newText = await translateText({
        text,
        targetLang,
      });
      setTranslatedText(newText);
      setIsTranslating(false);
      handleClose();
    } else {
      handleUpgrade();
    }
  };

  const handleCopySummary = () => {
    if (guardLimits('AISummaries')) {
      navigator.clipboard
        .writeText(translatedText)
        .then(() => {
          setCopySuccess(true);
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    } else {
      handleUpgrade();
    }
  };

  const handleEditSummary = () => {
    if (guardLimits('AISummaries')) {
      dispatch(toggleSummaryEdit());
    } else {
      handleUpgrade();
    }
  };

  const handleFeedbackOpen = () => {
    setFeedbackOpen(true);
  };

  const handleFeedbackClose = () => {
    setFeedbackOpen(false);
  };

  const handleFeedbackSubmit = () => {
    dispatch(reprocessSelectedVideo(selectedVideo.file._id));
    setFeedbackOpen(false);
  };

  const actions: UpdatedActionObject[] = [
    {
      action: 'Copy',
      icon: copySuccess ? <Check /> : <ContentCopy />,
      label: copySuccess ? 'Copied!' : 'Copy Summary',
      onClick: handleCopySummary,
    },
    {
      action: 'Edit',
      icon: <Edit />,
      label: 'Edit Summary',
      onClick: handleEditSummary,
    },
    {
      action: 'Translate',
      icon: <Translate />,
      label: 'Translate',
      onClick: handleTranslateClick,
    },
    {
      action: 'Reprocess',
      icon: <Autorenew />,
      label: 'Reprocess',
      onClick: handleFeedbackOpen,
    },
  ];

  const filteredText = useMemo(() => {
    if (!searchTerm) return translatedText;
    const regex = new RegExp(searchTerm, 'gi');
    return translatedText
      .split('\n')
      .filter((line) => regex.test(line))
      .join('\n');
  }, [translatedText, searchTerm]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(1),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(1),
          marginBottom: theme.spacing(1),
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexGrow: 1,
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.spacing(1),
            height: '40px', // Match the height of the pill box
          }}
        >
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '100%',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
              },
            }}
          />
        </Paper>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.spacing(1),
            height: '40px', // Explicitly set the height
          }}
        >
          {actions.map((action) => (
            <Tooltip
              title={action.label}
              placement="bottom"
              arrow
              key={action.action}
            >
              <IconButton
                type="button"
                aria-label={action.action}
                onClick={action.onClick}
                disabled={isTranslating}
                sx={{
                  padding: theme.spacing(0.5),
                  margin: theme.spacing(0.5),
                  color: theme.palette.text.primary,
                  borderRadius: '8px',
                  height: '30px',
                  width: '30px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Paper>
      </Box>
      {isTranslating ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Description text={filteredText} />
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {topLanguagesSorted.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => translate(language.code)}
          >
            {language.name}
          </MenuItem>
        ))}
      </Menu>
      <FeedbackModal
        open={feedbackOpen}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
      />
    </Box>
  );
};

export default SummaryViewOnly;
